import { get, toString } from 'lodash';

import { RecentSearch } from '~/features/advanced-search/types';
import { HomeSettings } from '~/features/home/utils/homeSettings';
import { ServiceRequestConfig } from '~/services/createService';
import { pilot } from '~/services/serviceInstances';
import { PilotId } from '~/types/PilotId';

import { SearchableFieldId } from './alert';

interface PinnedFiltersResponse {
  pinnedFilters?: {
    value?: Array<SearchableFieldId | PilotId>;
  };
}

interface PinnedFiltersReturnValue {
  data?: Array<SearchableFieldId>;
}

interface PinnedFiltersParams {
  context: string;
  pinnedFilters: Array<SearchableFieldId>;
}

interface RecentSearchesResponse {
  recentSearches?: {
    value?: Array<RecentSearch>;
  };
}

export type ContextType =
  | 'FOLDER'
  | 'SEARCH'
  | 'GROUP'
  | 'EVISEARCH_SEARCH'
  | 'GLOBAL';

export const getPinnedFilters = async (
  context: ContextType,
): Promise<PinnedFiltersReturnValue> => {
  const response: PinnedFiltersResponse = await pilot.get(
    '/user/preferences/',
    { params: { context } },
  );
  return { data: response?.pinnedFilters?.value?.map(toString) };
};

export const updatePinnedFilters = ({
  context,
  pinnedFilters,
}: PinnedFiltersParams): Promise<{}> => {
  return pilot.put(
    '/user/preferences/',
    { value: pinnedFilters },
    { params: { context, key: 'pinnedFilters' } },
  );
};

export const getRecentSearches = async ({
  context,
}: {
  context: ContextType;
}) => {
  const response: RecentSearchesResponse = await pilot.get(
    '/user/preferences/',
    {
      params: { context, key: 'recentSearches' },
    },
  );
  return { data: response?.recentSearches?.value };
};

export const updateRecentSearches = ({
  context,
  recentSearches,
}: {
  context: ContextType;
  recentSearches: RecentSearch[];
}): Promise<{}> => {
  return pilot.put(
    '/user/preferences/',
    { value: recentSearches },
    { params: { context, key: 'recentSearches' } },
  );
};

export const getLocalizationPreferences = async () => {
  const response = await pilot.get('/user/preferences/', {
    params: { key: 'i18n', context: 'GLOBAL' },
  });

  return { data: get(response, 'i18n.value', {}) };
};

export const updateLocalizationPreferences = async ({
  language,
  locale,
}: {
  language?: string;
  locale?: string;
}) => {
  await pilot.put(
    '/user/preferences/',
    { value: { language, locale } },
    { params: { key: 'i18n', context: 'GLOBAL' } },
  );
};

interface HomeSettingsResponse {
  homeSettings?: {
    // Not typed as HomeSettings -- this is unvalidated server JSON (see
    // mergeHomeSettings, which is what actually establishes the shape). A
    // real account had this holding a completely different feature's
    // payload; asserting a precise type here would just repeat the false
    // guarantee that caused HomeSettingsPanel to crash on it.
    value?: unknown;
  };
}

export const getHomeSettings = async (
  _?: void,
): Promise<{ data?: unknown }> => {
  const response: HomeSettingsResponse = await pilot.get('/user/preferences/', {
    params: { context: 'GLOBAL', key: 'homeSettings' },
  });
  return { data: response?.homeSettings?.value };
};

export const updateHomeSettings = (
  { homeSettings }: { homeSettings: HomeSettings },
  options: ServiceRequestConfig = {},
): Promise<{}> => {
  return pilot.put(
    '/user/preferences/',
    { value: homeSettings },
    // params last -- context/key identify which preference row this writes
    // to, so a caller-supplied rtkQueryOptions.params must never be able to
    // override them.
    { ...options, params: { context: 'GLOBAL', key: 'homeSettings' } },
  );
};
